import { useState } from 'react';
import { useAdminAuth } from '../../../hooks/useAdminAuth';

interface Plan {
  name: string;
  price: string;
  annualPrice: string;
  period: string;
  annualPeriod: string;
  features: string[];
}

interface UpgradePromptModalProps {
  wordsUsed: number;
  freeLimit: number;
  onUpgrade: (plan: Plan) => void;
  onDismiss: () => void;
}

const PRO_PLAN: Plan = {
  name: 'Pro',
  price: '$39',
  annualPrice: '$19',
  period: '/month',
  annualPeriod: '/month, billed annually',
  features: [
    'Unlimited AI generations',
    'Access to all 10 tools',
    'Premium templates',
    'High-quality output',
    'Priority support',
    'No watermarks',
    'Export to PDF, DOCX, PPTX',
    'Custom branding',
  ],
};

const GOLDEN_PRO_PLAN: Plan = {
  name: 'Golden Pro',
  price: '$49',
  annualPrice: '$39',
  period: '/month',
  annualPeriod: '/month, billed annually',
  features: [
    'Everything in Pro',
    'Team collaboration (5 seats)',
    'API access',
    'White-label exports',
    'Advanced analytics',
    'Dedicated account manager',
    'Custom AI fine-tuning',
    'SLA guarantee',
    'Early access to new tools',
  ],
};

export default function UpgradePromptModal({
  wordsUsed,
  freeLimit,
  onUpgrade,
  onDismiss,
}: UpgradePromptModalProps) {
  const { isAdmin } = useAdminAuth();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const overage = Math.max(0, wordsUsed - freeLimit);
  const pct = Math.min((wordsUsed / freeLimit) * 100, 100);

  // Admin never sees upgrade prompts
  if (isAdmin) {
    onDismiss();
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onDismiss}></div>

      {/* Modal */}
      <div
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden"
        style={{ animation: 'upgradeIn 0.3s cubic-bezier(0.34,1.56,0.64,1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 px-8 pt-8 pb-6 overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/10"></div>
          <div className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-white/10"></div>

          <div className="relative z-10 flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm flex-shrink-0">
                <i className="ri-fire-fill text-white text-2xl"></i>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2.5 py-0.5 rounded-full bg-white/20 text-white text-xs font-bold uppercase tracking-widest">Free Limit Reached</span>
                </div>
                <h2 className="text-white text-2xl font-extrabold leading-tight">
                  You&apos;ve used {wordsUsed.toLocaleString()} free words
                </h2>
                <p className="text-white/80 text-sm mt-1">
                  {overage > 0
                    ? `${overage.toLocaleString()} words over your free limit — upgrade to keep creating`
                    : `You&apos;ve hit the ${freeLimit.toLocaleString()}-word free limit — unlock unlimited access`}
                </p>
              </div>
            </div>
            <button
              onClick={onDismiss}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors cursor-pointer flex-shrink-0 mt-1"
            >
              <i className="ri-close-line text-white text-lg"></i>
            </button>
          </div>

          {/* Usage bar */}
          <div className="relative z-10 mt-5">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-white/80 text-xs font-semibold">Free word usage</span>
              <span className="text-white text-xs font-bold">{wordsUsed.toLocaleString()} / {freeLimit.toLocaleString()} words</span>
            </div>
            <div className="w-full h-2.5 rounded-full bg-white/25 overflow-hidden">
              <div
                className="h-full rounded-full bg-white transition-all duration-500"
                style={{ width: `${pct}%` }}
              ></div>
            </div>
            <div className="flex items-center gap-1.5 mt-2">
              <i className="ri-lock-line text-white/70 text-xs"></i>
              <span className="text-white/70 text-xs">Upgrade for unlimited generations with no word caps</span>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {/* Billing toggle */}
          <div className="flex items-center justify-between mb-5">
            <p className="text-sm font-bold text-gray-700">Choose your plan</p>
            <div className="inline-flex items-center gap-1 bg-gray-100 rounded-full px-1 py-1">
              <button
                onClick={() => setBillingCycle('monthly')}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${billingCycle === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingCycle('annual')}
                className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all cursor-pointer whitespace-nowrap ${billingCycle === 'annual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Annual
                <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">Save 50%</span>
              </button>
            </div>
          </div>

          {/* Plan cards */}
          <div className="grid grid-cols-2 gap-4">
            {/* Pro */}
            <div className="relative rounded-2xl bg-gradient-to-b from-violet-600 to-cyan-600 p-5 flex flex-col gap-3 text-white">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full text-xs font-extrabold text-white shadow whitespace-nowrap">
                ⭐ Most Popular
              </div>
              <div>
                <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/20 mb-2">
                  <i className="ri-rocket-line text-white text-base"></i>
                </div>
                <p className="text-sm font-extrabold">Pro</p>
                <div className="flex items-end gap-1 mt-0.5">
                  <span className="text-2xl font-extrabold">
                    {billingCycle === 'annual' ? PRO_PLAN.annualPrice : PRO_PLAN.price}
                  </span>
                  <span className="text-xs mb-1 text-white/70">
                    {billingCycle === 'annual' ? '/mo, billed annually' : '/month'}
                  </span>
                </div>
                {billingCycle === 'annual' && (
                  <div className="flex items-center gap-1 mt-1">
                    <i className="ri-price-tag-3-fill text-amber-300 text-xs"></i>
                    <span className="text-xs font-bold text-white">Save $240/year</span>
                  </div>
                )}
              </div>
              <ul className="space-y-1.5 flex-1">
                {PRO_PLAN.features.slice(0, 5).map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <i className="ri-check-line text-cyan-200 text-xs flex-shrink-0 mt-0.5"></i>
                    <span className="text-xs text-white/85 leading-snug">{f}</span>
                  </li>
                ))}
                <li className="text-xs text-white/50">+{PRO_PLAN.features.length - 5} more benefits</li>
              </ul>
              <button
                onClick={() => onUpgrade(PRO_PLAN)}
                className="w-full py-3 rounded-xl bg-white text-violet-700 text-sm font-bold hover:bg-gray-50 hover:shadow-lg transition-all cursor-pointer whitespace-nowrap"
              >
                Upgrade to Pro
              </button>
            </div>

            {/* Golden Pro */}
            <div className="relative rounded-2xl bg-gradient-to-b from-gray-800 to-gray-900 p-5 flex flex-col gap-3 text-white border-2 border-amber-400/50">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-500 to-yellow-400 rounded-full text-xs font-extrabold text-gray-900 shadow whitespace-nowrap">
                👑 Best Value
              </div>
              <div>
                <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-400/20 mb-2">
                  <i className="ri-building-2-line text-amber-400 text-base"></i>
                </div>
                <p className="text-sm font-extrabold">Golden Pro</p>
                <div className="flex items-end gap-1 mt-0.5">
                  <span className="text-2xl font-extrabold">
                    {billingCycle === 'annual' ? GOLDEN_PRO_PLAN.annualPrice : GOLDEN_PRO_PLAN.price}
                  </span>
                  <span className="text-xs mb-1 text-white/70">
                    {billingCycle === 'annual' ? '/mo, billed annually' : '/month'}
                  </span>
                </div>
                {billingCycle === 'annual' && (
                  <p className="text-xs font-bold text-amber-400 mt-1">Save $120/year</p>
                )}
              </div>
              <ul className="space-y-1.5 flex-1">
                {GOLDEN_PRO_PLAN.features.slice(0, 5).map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <i className="ri-check-line text-amber-400 text-xs flex-shrink-0 mt-0.5"></i>
                    <span className="text-xs text-white/85 leading-snug">{f}</span>
                  </li>
                ))}
                <li className="text-xs text-white/50">+{GOLDEN_PRO_PLAN.features.length - 5} more benefits</li>
              </ul>
              <button
                onClick={() => onUpgrade(GOLDEN_PRO_PLAN)}
                className="w-full py-3 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-400 text-gray-900 text-sm font-bold hover:shadow-lg hover:scale-[1.02] transition-all cursor-pointer whitespace-nowrap"
              >
                Upgrade to Golden Pro
              </button>
            </div>
          </div>

          {/* What you lose without upgrade */}
          <div className="mt-4 p-4 rounded-2xl bg-gray-50 border border-gray-100 flex items-start gap-3">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-100 flex-shrink-0 mt-0.5">
              <i className="ri-information-line text-rose-500 text-sm"></i>
            </div>
            <div>
              <p className="text-xs font-bold text-gray-800 mb-1">On the Free plan you&apos;re limited to:</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {['1 AI generation/month', '3 tools only', 'Watermarked exports', 'Standard quality output'].map((l) => (
                  <div key={l} className="flex items-center gap-1.5">
                    <i className="ri-close-circle-line text-rose-400 text-xs"></i>
                    <span className="text-xs text-gray-500">{l}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={onDismiss}
              className="text-sm text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              Maybe later
            </button>
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <i className="ri-shield-check-fill text-emerald-500 text-sm"></i>
              30-day money-back guarantee
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes upgradeIn {
          from { opacity: 0; transform: scale(0.92) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
